import {
  addDays,
  differenceInSeconds,
  format as formatDate,
  formatDistanceStrict,
  isAfter,
  isBefore,
  isEqual,
  isPast,
  isThisYear,
  isToday,
  isTomorrow,
  isYesterday,
  isThisWeek,
  isThisMonth,
  startOfWeek,
  startOfMonth,
  parseISO,
  getDate,
  getMonth,
  getYear,
} from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { pluralize } from './string';

const mapping = {
  'US/Pacific': 'America/Los_Angeles',
  'US/Eastern': 'America/New_York',
};

export const timelineDateDisplay = (occasion: Occasion) => {
  const { value, unit } = occasion.elapsedTime();
  let prefix = '';

  if (['minute', 'minutes'].includes(unit) && value < 2) {
    return 'now';
  }

  if (value === 0) {
    return '';
  }

  if (!occasion.isPast) {
    prefix = 'in ';
    if (['month', 'months', 'year', 'years'].includes(unit)) {
      prefix = 'on ';
    }
  }

  if (['minute', 'minutes', 'hour', 'hours', 'day', 'days'].includes(unit)) {
    return `${prefix}${Math.abs(value)}${unit[0]}`;
  } else if (occasion.isThisYear) {
    return prefix + occasion.format('MMM d');
  }

  return prefix + occasion.format('MMM d yyyy');
};

export default class Occasion {
  static timeFormat = 'h:mm a';
  static dateFormat = 'MMM dd yyyy';
  static dateTimeFormat = 'MMM dd yyyy, h:mm a';
  static dateTimeFormatThisYear = 'MMM dd, h:mm a';
  static parsableDateFormat = 'yyyy-MM-dd';
  static parsableTimeFormat = 'HH:mm';
  static timezone = undefined;
  date: Date;

  constructor(date: Date | Occasion | string | number = new Date()) {
    if (typeof date === 'string') {
      this.date = parseISO(date);
    } else if (
      date instanceof Occasion ||
      (date && typeof date == 'object' && !(date instanceof Date))
    ) {
      this.date = date?.date;
    } else if (typeof date == 'number') {
      this.date = new Date(date * 1000);
    } else {
      this.date = date;
    }

    if (this.date && this.date.toString() == 'Invalid Date') {
      // we should log the bad date inputs here
      this.date = null;
    }

    if (Occasion.timezone) {
      this.date = utcToZonedTime(this.date, mapping[Occasion.timezone] || Occasion.timezone);
    }
  }

  static userTZ(timezone) {
    Occasion.timezone = timezone;
  }

  static fromTimestamp(ts) {
    return new Occasion(new Date(ts * 1000));
  }

  relative(compare = new Date()) {
    if (!this.date) {
      return '';
    }

    return formatDistanceStrict(this.date, compare, {
      addSuffix: true,
    });
  }

  get isValid(): boolean {
    return !!this.date;
  }

  get isToday(): boolean {
    return this.isValid && isToday(this.date);
  }

  get isYesterday(): boolean {
    return this.isValid && isYesterday(this.date);
  }

  get isTomorrow(): boolean {
    return this.isValid && isTomorrow(this.date);
  }

  get isThisWeek(): boolean {
    return this.isValid && isThisWeek(this.date);
  }

  get isLastWeek(): boolean {
    const _startOfWeek = startOfWeek(new Date());
    const startOfLastWeek = addDays(_startOfWeek, -7);
    return this.isValid && isAfter(this.date, startOfLastWeek) && isBefore(this.date, _startOfWeek);
  }

  get isThisMonth(): boolean {
    return this.isValid && isThisMonth(this.date);
  }

  get isLastMonth(): boolean {
    const _startOfMonth = startOfMonth(new Date());
    const endOfLastMonth = addDays(_startOfMonth, -1);
    const startOfLastMonth = startOfMonth(endOfLastMonth);
    return (
      this.isValid && isAfter(this.date, startOfLastMonth) && isBefore(this.date, _startOfMonth)
    );
  }

  get isThisYear(): boolean {
    return this.isValid && isThisYear(this.date);
  }

  get isPast(): boolean {
    return this.isValid && isPast(this.date);
  }

  get timeFromNow(): string {
    if (this.isToday) {
      return 'Today';
    } else if (this.isTomorrow) {
      return 'Tmrw';
    } else if (this.isYesterday) {
      return 'Yesterday';
    } else if (this.isUpcoming(7)) {
      return this.format('EEE');
    } else if (this.isThisYear) {
      return this.format('MMM d');
    }

    return this.format('MMM d yyyy');
  }

  get relativeTime(): string {
    if (this.isToday) {
      return 'Today';
    } else if (this.isTomorrow) {
      return 'Tmrw';
    } else if (this.isYesterday) {
      return 'Yesterday';
    }

    return this.relativeDuration;
  }

  get relativeDuration(): string {
    return this.relative();
  }

  get dateParts(): { year: number; month: number; day: number } {
    return {
      year: getYear(this.date),
      month: getMonth(this.date),
      day: getDate(this.date),
    };
  }

  isEqual(otherOccasion?: Occasion): boolean {
    if (!otherOccasion || !otherOccasion.isValid || !this.isValid) {
      return false;
    }

    return isEqual(this.date, otherOccasion.date);
  }

  isUpcoming(days = 14) {
    const upcoming = new Date();
    upcoming.setDate(upcoming.getDate() + days);

    return this.isInRange(new Date(), upcoming);
  }

  isWithin(days = 7) {
    const past = new Date();
    past.setDate(past.getDate() - days);

    return this.isInRange(past, new Date());
  }

  isInRange(start, end) {
    return this.date >= start && this.date <= end;
  }

  isBefore(otherOccasion: Occasion) {
    return isBefore(this.date, otherOccasion.date);
  }

  format(format = '') {
    if (!this.isValid) {
      return '';
    }

    return formatDate(this.date, format);
  }

  get unix() {
    return Math.floor(this.date.getTime() / 1000);
  }

  duration(date) {
    if (!this.isValid) {
      return '';
    }

    const duration = Math.abs((date.date.getTime() - this.date.getTime()) / 1000);

    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = Math.floor((duration % 3600) % 60);

    if (hours === 1) {
      return '60M';
    } else if (hours > 1) {
      return `${hours}H`;
    } else if (minutes) {
      return `${minutes}M`;
    }

    return seconds;
  }

  differenceInSeconds(compare = new Date()) {
    return differenceInSeconds(compare, this.date);
  }

  elapsedTime(): { value: number; unit: string } {
    if (!this.isValid) {
      return { value: -1, unit: '' };
    }

    const now = new Occasion();
    const elapsedHours = Math.abs((now.date.getTime() - this.date.getTime()) / (1000 * 60 * 60));
    const elapsedDays = elapsedHours / 24;
    const elapsedYears = elapsedDays / 365;
    const elapsedMonths = elapsedYears * 12;

    if (elapsedHours < 1) {
      const value = Math.round(this.differenceInSeconds() / 60);
      return { value, unit: pluralize(value, 'minute') };
    } else if (elapsedHours < 24) {
      const value = Math.round(elapsedHours);
      return { value, unit: pluralize(value, 'hour') };
    } else if (elapsedMonths < 1) {
      const value = Math.round(elapsedDays);
      return { value, unit: pluralize(value, 'day') };
    } else if (elapsedMonths < 10) {
      const value = Math.round(elapsedMonths);
      return { value, unit: pluralize(value, 'month') };
    } else {
      const value = Math.round(elapsedYears);
      return { value, unit: pluralize(value, 'year') };
    }
  }

  addDays(days: number): Date {
    return addDays(this.date, days);
  }

  toString() {
    return this.date ? this.date.toISOString() : '';
  }

  toJSON() {
    return this.toString();
  }
}
